import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, DateInput, Modal, useConfirmDialog } from '@safetyhub/ui';
import { formatDateISO } from '@safetyhub/shared';
import { ModalCancelButton } from '../components/ModalCancelButton';
import { adminApi, SmsDailyStatsItem, SmsSendLogItem, SmsPrefix, extractApiError } from '../lib/api';
import { useToastStore } from '../stores/toast';
import { Badge, type BadgeColor } from '../components/Badge';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface SmsBalance {
  sms: number;
  lms: number;
  mms: number;
}

interface SmsStatus {
  provider: string;
  connected: boolean;
  balance: SmsBalance | null;
  error?: string;
  checkedAt: string;
}

interface ExternalServicesStatus {
  sms: SmsStatus;
}

type TabKey = 'sms' | 'ai';

type DateRangeOption = 'this_week' | 'this_month' | 'last_3_months' | 'last_6_months' | 'last_1_year';

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const TABS: { key: TabKey; label: string }[] = [
  { key: 'sms', label: 'SMS 서비스' },
  { key: 'ai', label: 'AI 서비스' },
];

const DATE_RANGE_OPTIONS: { value: DateRangeOption; label: string }[] = [
  { value: 'this_week', label: '이번 주' },
  { value: 'this_month', label: '이번 달' },
  { value: 'last_3_months', label: '최근 3개월' },
  { value: 'last_6_months', label: '최근 6개월' },
  { value: 'last_1_year', label: '최근 1년' },
];

const BALANCE_ITEMS = [
  { key: 'sms', label: 'SMS (단문)' },
  { key: 'lms', label: 'LMS (장문)' },
  { key: 'mms', label: 'MMS (사진)' },
] as const;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function getBalanceStyle(count: number) {
  if (count <= 50) return { color: 'text-red-600', bg: 'bg-red-50 border-red-200' };
  if (count <= 200) return { color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' };
  return { color: 'text-green-600', bg: 'bg-green-50 border-green-200' };
}

/**
 * 조회 기간 — 날짜 키는 **현지(KST) 날짜**다. 종전 `toISOString().split('T')[0]`은 UTC 날짜라
 * KST 00:00~09:00 사이에는 「오늘」이 어제로, 시작일도 하루 앞으로 밀렸다(리뷰 1103 F5).
 * shared `formatDateISO`(기본 `Asia/Seoul`)가 정본이다.
 */
export function getDateRange(
  option: DateRangeOption,
  now: Date = new Date(),
): { startDate: string; endDate: string } {
  const endDate = formatDateISO(now);
  let startDate: string;

  switch (option) {
    case 'this_week': {
      const start = new Date(now);
      // Monday as start of week
      const day = now.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(now.getDate() + diff);
      startDate = formatDateISO(start);
      break;
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate = formatDateISO(start);
      break;
    }
    case 'last_3_months': {
      const start = new Date(now);
      start.setMonth(now.getMonth() - 3);
      startDate = formatDateISO(start);
      break;
    }
    case 'last_6_months': {
      const start = new Date(now);
      start.setMonth(now.getMonth() - 6);
      startDate = formatDateISO(start);
      break;
    }
    case 'last_1_year': {
      const start = new Date(now);
      start.setFullYear(now.getFullYear() - 1);
      startDate = formatDateISO(start);
      break;
    }
  }

  return { startDate, endDate };
}

function formatShortDate(dateStr: string): string {
  // e.g. "2026-03-20" -> "03-20"
  return dateStr.slice(5);
}

/**
 * DB에 저장된 UTC 날짜를 KST 문자열로 변환
 * includeTime=false (기본): "2026-03-20"
 * includeTime=true:        "2026-03-20 15:30:00"
 *
 * TODO: @safetyhub/shared의 formatDateISO / formatDateTimeISO 로 교체
 * (formatDateISO → "YYYY-MM-DD", formatDateTimeISO → "YYYY-MM-DDTHH:mm" 포맷 확인 후 적용)
 */
function toKstString(dateStr: string, includeTime = false): string {
  const d = new Date(dateStr);
  const kst = new Date(d.getTime() + 9 * 3600000);
  const y = kst.getUTCFullYear();
  const m = String(kst.getUTCMonth() + 1).padStart(2, '0');
  const day = String(kst.getUTCDate()).padStart(2, '0');
  if (!includeTime) return `${y}-${m}-${day}`;
  const h = String(kst.getUTCHours()).padStart(2, '0');
  const min = String(kst.getUTCMinutes()).padStart(2, '0');
  const sec = String(kst.getUTCSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${sec}`;
}

// Badge: 공통 컴포넌트 import (../components/Badge)

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className ?? 'w-4 h-4'}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'w-4 h-4'} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

// ─────────────────────────────────────────────
// SMS Status Section
// ─────────────────────────────────────────────

function SmsStatusSection({
  status,
  loading,
}: {
  status: ExternalServicesStatus | undefined;
  loading: boolean;
}) {
  return (
    <Card padding="xl" radius="lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-brand-100 rounded-control flex items-center justify-center">
            <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">SMS 서비스 상태</h2>
            <p className="text-sm text-gray-500">Provider: {status?.sms?.provider || '-'}</p>
          </div>
        </div>
        {status && (
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            status.sms.connected
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {status.sms.connected ? '연결됨' : '연결 실패'}
          </span>
        )}
      </div>

      {loading && !status ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <SpinnerIcon className="w-6 h-6 mr-2" />
          로딩 중...
        </div>
      ) : status?.sms.connected && status.sms.balance ? (
        <div className="grid grid-cols-3 gap-4">
          {BALANCE_ITEMS.map(({ key, label }) => {
            const count = status.sms.balance![key];
            const { bg, color } = getBalanceStyle(count);
            return (
              <div key={key} className={`p-4 rounded-card border ${bg}`}>
                <p className="text-sm text-gray-600 mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>
                  {count.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">발송 가능 건수</p>
              </div>
            );
          })}
        </div>
      ) : status?.sms && !status.sms.connected ? (
        <div className="p-4 bg-red-50 rounded-card text-red-700 text-sm">
          서비스 연결에 실패했습니다. {status.sms.error && `(${status.sms.error})`}
        </div>
      ) : null}

      {status?.sms.checkedAt && (
        <p className="mt-4 text-xs text-gray-400 text-right">
          마지막 확인: {new Date(status.sms.checkedAt).toLocaleString('ko-KR')}
        </p>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────
// Collect Modal
// ─────────────────────────────────────────────

function CollectModal({
  onClose,
  onCollect,
  isCollecting,
}: {
  onClose: () => void;
  onCollect: (date: string) => void;
  isCollecting: boolean;
}) {
  // 현지(KST) 오늘 — UTC 날짜면 KST 오전 9시 전에 어제가 기본값이 된다(위 `getDateRange` JSDoc)
  const today = formatDateISO(new Date());
  const [selectedDate, setSelectedDate] = useState(today);

  // 부모가 열 때만 마운트하므로 open은 늘 true다. 수집 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다.
  return (
    <Modal
      open
      onClose={onClose}
      title="수동 통계 수집"
      size="sm"
      closeOnOverlay={!isCollecting}
      closeOnEsc={!isCollecting}
      showCloseButton={!isCollecting}
      closeLocked={isCollecting}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isCollecting}>
            취소
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onCollect(selectedDate)}
            disabled={isCollecting || !selectedDate}
          >
            {isCollecting && <SpinnerIcon />}
            수집
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-500 mb-4">
        수집할 날짜를 선택하세요. 해당 날짜의 발송 통계를 가져옵니다.
      </p>
      <DateInput
        label="수집 날짜"
        value={selectedDate}
        max={today}
        onChange={setSelectedDate}
        className="w-full"
      />
    </Modal>
  );
}

// ─────────────────────────────────────────────
// SMS Type Badge
// ─────────────────────────────────────────────

function SmsTypeBadge({ type }: { type: string }) {
  const upper = type.toUpperCase();
  const colorMap: Record<string, BadgeColor> = { SMS: 'blue', LMS: 'purple', MMS: 'green' };
  const color: BadgeColor = colorMap[upper] ?? 'gray';
  return <Badge color={color} text={upper} />;
}

// ─────────────────────────────────────────────
// Send Logs Modal
// ─────────────────────────────────────────────

function SmsStateBadge({ state }: { state?: string }) {
  if (!state) return <span className="text-gray-400 text-xs">-</span>;
  if (state === '발송완료') return <Badge color="green" text={state} />;
  if (state === '실패' || state.includes('실패')) return <Badge color="red" text={state} />;
  return <Badge color="gray" text={state} />;
}

function SendLogsModal({
  date,
  onClose,
}: {
  date: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'sms-send-logs', date],
    queryFn: async (): Promise<{ logs: SmsSendLogItem[] }> => {
      const res = await adminApi.getSmsSendLogs(date!);
      if (!res.data) throw new Error('발송 로그 조회에 실패했습니다.');
      return res.data;
    },
    enabled: date !== null,
  });

  if (date === null) return null;

  const kstDate = toKstString(date);

  // 스크롤 잠금은 base가 소유한다(종전 이 컴포넌트의 body overflow effect를 걷었다 — 두 벌이면
  // 닫힐 때 서로의 복원값을 덮는다). 헤더·푸터 고정 + 본문 스크롤은 base `bodyScroll`(기본)이 맡는다.
  // 폭은 base 최대(`xl` = max-w-2xl)보다 넓어야 해(표 5열) `!max-w-4xl`로 덮는다 — 같은 property를
  // 덧칠하면 승자가 Tailwind 출력 순서에 달리므로 `!`로 우선을 고정한다(선례 `ManualDoc`의 `!max-w-[92vw]`).
  return (
    <Modal
      open
      onClose={onClose}
      title={`${kstDate} 발송 내역`}
      size="xl"
      panelClassName="!max-w-4xl"
      footer={
        <Button variant="secondary" size="sm" onClick={onClose}>
          닫기
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-gray-400">
          <SpinnerIcon className="w-5 h-5" />
          <span className="text-sm">발송 로그 불러오는 중...</span>
        </div>
      ) : error ? (
        <div className="py-8 text-sm text-red-600 text-center">
          {extractApiError(error).message}
        </div>
      ) : !data || data.logs.length === 0 ? (
        <div className="py-12 text-sm text-gray-400 text-center">
          해당 날짜의 발송 로그가 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2.5 text-left font-medium text-gray-600 whitespace-nowrap w-40">발송일시 (KST)</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600 w-16">타입</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600 whitespace-nowrap w-32">수신번호</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600 w-24">발송상태</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">메시지 내용</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.logs.map((log) => (
                <tr key={log.mid} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-600 font-mono whitespace-nowrap text-xs w-40">
                    {toKstString(log.sendDate ?? log.sentAt, true)}
                  </td>
                  <td className="px-4 py-2.5 w-16">
                    <SmsTypeBadge type={log.type} />
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 font-mono whitespace-nowrap w-32">{log.receiver}</td>
                  <td className="px-4 py-2.5 w-24">
                    <SmsStateBadge state={log.smsState} />
                  </td>
                  <td className="px-4 py-2.5 text-gray-700" title={log.message}>
                    {log.message.length > 150 ? `${log.message.slice(0, 150)}...` : log.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────
// Add Prefix Modal
// ─────────────────────────────────────────────

function AddPrefixModal({
  onClose,
  onAdd,
  isAdding,
}: {
  onClose: () => void;
  onAdd: (data: { prefix: string; label?: string; serviceKey?: string }) => void;
  isAdding: boolean;
}) {
  const [prefix, setPrefix] = useState('');
  const [label, setLabel] = useState('');
  const [serviceKey, setServiceKey] = useState('');
  const prefixRef = useRef<HTMLInputElement>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!prefix.trim()) return;
    onAdd({
      prefix: prefix.trim(),
      label: label.trim() || undefined,
      serviceKey: serviceKey.trim() || undefined,
    });
  }

  // 부모가 열 때만 마운트하므로 open은 늘 true다. 추가 중에는 닫는 길(오버레이·Esc·✕) 전부를 막고,
  // 입력이 있으면 닫기 확인을 거친다(종전엔 바깥 클릭·Esc로 닫히지 않아 입력을 잃을 길이 취소뿐이었다).
  // 첫 포커스는 `autoFocus` 대신 `initialFocusRef` — base가 열림 직후 패널로 포커스를 옮기며 덮는다.
  return (
    <Modal
      open
      onClose={onClose}
      title="SMS 접두사 추가"
      size="sm"
      initialFocusRef={prefixRef}
      isDirty={prefix !== '' || label !== '' || serviceKey !== ''}
      closeOnOverlay={!isAdding}
      closeOnEsc={!isAdding}
      showCloseButton={!isAdding}
      closeLocked={isAdding}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            접두사 <span className="text-red-500">*</span>
          </label>
          <input
            ref={prefixRef}
            type="text"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="예: [SafetyHub]"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            라벨 <span className="text-gray-400 font-normal">(선택)</span>
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="예: SafetyHub 기본"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            서비스 키 <span className="text-gray-400 font-normal">(선택, 미입력 시 자동생성)</span>
          </label>
          <input
            type="text"
            value={serviceKey}
            onChange={(e) => setServiceKey(e.target.value)}
            placeholder="예: safetyhub"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <ModalCancelButton onCancel={onClose} disabled={isAdding} size="sm" />
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={isAdding || !prefix.trim()}
          >
            {isAdding && <SpinnerIcon />}
            추가
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────
// SMS Prefix Section
// ─────────────────────────────────────────────

function SmsPrefixSection() {
  const [confirm, confirmDialog] = useConfirmDialog();
  const [showAddModal, setShowAddModal] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'sms-prefixes'],
    queryFn: async (): Promise<SmsPrefix[]> => {
      const res = await adminApi.getSmsPrefixes();
      if (!res.data) throw new Error('접두사 목록 조회에 실패했습니다.');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (d: { prefix: string; label?: string; serviceKey?: string }) =>
      adminApi.createSmsPrefix(d),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sms-prefixes'] });
      setShowAddModal(false);
      showToast('접두사가 추가되었습니다.', 'success');
    },
    onError: (err) => {
      showToast(extractApiError(err).message, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { label?: string; isActive?: boolean; serviceKey?: string } }) =>
      adminApi.updateSmsPrefix(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sms-prefixes'] });
      showToast('접두사가 업데이트되었습니다.', 'success');
    },
    onError: (err) => {
      showToast(extractApiError(err).message, 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteSmsPrefix(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sms-prefixes'] });
      showToast('접두사가 삭제되었습니다.', 'success');
    },
    onError: (err) => {
      showToast(extractApiError(err).message, 'error');
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => adminApi.setDefaultSmsPrefix(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sms-prefixes'] });
      showToast('기본 접두사가 변경되었습니다.', 'success');
    },
    onError: (err) => {
      showToast(extractApiError(err).message, 'error');
    },
  });

  const prefixes = data ?? [];
  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    setDefaultMutation.isPending;

  return (
    <Card padding="xl" radius="lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">SMS 접두사 관리</h2>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowAddModal(true)}
          disabled={isMutating}
        >
          추가
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {extractApiError(error).message}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <SpinnerIcon className="w-5 h-5 mr-2" />
          로딩 중...
        </div>
      ) : prefixes.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">
          등록된 접두사가 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">접두사</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">라벨</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">서비스 키</th>
                <th className="px-4 py-2.5 text-center font-medium text-gray-600">기본</th>
                <th className="px-4 py-2.5 text-center font-medium text-gray-600">활성</th>
                <th className="px-4 py-2.5 text-center font-medium text-gray-600">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {prefixes.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-800 font-mono font-medium">{p.prefix}</td>
                  <td className="px-4 py-2.5 text-gray-700">{p.label || <span className="text-gray-400">-</span>}</td>
                  <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{p.serviceKey || <span className="text-gray-400">-</span>}</td>
                  <td className="px-4 py-2.5 text-center">
                    {p.isDefault ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    ) : (
                      <button
                        onClick={() => setDefaultMutation.mutate(p.id)}
                        disabled={isMutating}
                        className="text-xs text-brand-600 hover:text-brand-800 hover:underline disabled:opacity-50 disabled:no-underline"
                      >
                        기본 설정
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {p.isActive ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {p.isDefault ? (
                      <span className="text-xs text-gray-400">-</span>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => updateMutation.mutate({ id: p.id, data: { isActive: !p.isActive } })}
                          disabled={isMutating}
                          className="text-xs text-brand-600 hover:text-brand-800 hover:underline disabled:opacity-50"
                        >
                          {p.isActive ? '비활성화' : '활성화'}
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={async () => {
                            if (
                              await confirm({
                                title: '접두사 삭제',
                                body: `"${p.prefix}" 접두사를 삭제하시겠습니까?`,
                                confirmLabel: '삭제',
                                danger: true,
                              })
                            ) {
                              deleteMutation.mutate(p.id);
                            }
                          }}
                          disabled={isMutating}
                          className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                        >
                          삭제
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <AddPrefixModal
          onClose={() => setShowAddModal(false)}
          onAdd={(d) => createMutation.mutate(d)}
          isAdding={createMutation.isPending}
        />
      )}
      {confirmDialog}
    </Card>
  );
}

// ─────────────────────────────────────────────
// SMS Daily Stats Section
// ─────────────────────────────────────────────

function SmsDailyStatsSection() {
  const [dateRange, setDateRange] = useState<DateRangeOption>('this_week');
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { startDate, endDate } = getDateRange(dateRange);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['admin', 'sms-daily-stats', startDate, endDate],
    queryFn: async (): Promise<SmsDailyStatsItem[]> => {
      const res = await adminApi.getSmsDailyStats(startDate, endDate);
      if (!res.data) throw new Error('통계 조회에 실패했습니다.');
      return res.data;
    },
  });

  const collectMutation = useMutation({
    mutationFn: (date: string) => adminApi.collectSmsDailyStats(date),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sms-daily-stats'] });
      setShowCollectModal(false);
    },
  });

  const stats = data ?? [];

  const totals = stats.reduce(
    (acc, row) => ({
      sms: acc.sms + row.smsCount,
      lms: acc.lms + row.lmsCount,
      mms: acc.mms + row.mmsCount,
      total: acc.total + row.totalCount,
    }),
    { sms: 0, lms: 0, mms: 0, total: 0 },
  );

  function handleRowClick(date: string) {
    setSelectedDate(date);
  }

  return (
    <Card padding="xl" radius="lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">일별 발송 통계</h2>
        <Button variant="secondary" size="sm" onClick={() => setShowCollectModal(true)}>
          수동 수집
        </Button>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 mb-4">
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          {DATE_RANGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <span className="text-sm text-gray-500">
          기간: {formatShortDate(startDate)} ~ {formatShortDate(endDate)}
        </span>
        {isFetching && <SpinnerIcon className="w-4 h-4 text-gray-400" />}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {extractApiError(error).message}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <SpinnerIcon className="w-6 h-6 mr-2" />
          로딩 중...
        </div>
      ) : stats.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-400">
          해당 기간의 통계 데이터가 없습니다.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">날짜</th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-600">SMS</th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-600">LMS</th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-600">MMS</th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-600">합계</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.map((row) => {
                  const kstDate = toKstString(row.date);
                  return (
                    <tr
                      key={row.date}
                      onClick={() => handleRowClick(row.date)}
                      className="hover:bg-brand-50 cursor-pointer select-none"
                    >
                      <td className="px-4 py-2.5 text-gray-700 font-mono">{kstDate}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{row.smsCount.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{row.lmsCount.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{row.mmsCount.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{row.totalCount.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary row */}
          <div className="mt-4 pt-3 border-t border-gray-200 text-sm text-gray-600">
            합계: SMS {totals.sms.toLocaleString()}건 / LMS {totals.lms.toLocaleString()}건 / MMS {totals.mms.toLocaleString()}건 = <span className="font-semibold text-gray-900">{totals.total.toLocaleString()}건</span>
          </div>
        </>
      )}

      {showCollectModal && (
        <CollectModal
          onClose={() => setShowCollectModal(false)}
          onCollect={(date) => collectMutation.mutate(date)}
          isCollecting={collectMutation.isPending}
        />
      )}

      <SendLogsModal
        date={selectedDate}
        onClose={() => setSelectedDate(null)}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────
// AI Tab Placeholder
// ─────────────────────────────────────────────

function AiServiceTab() {
  return (
    <div className="bg-white rounded-card-lg shadow-sm border p-12 text-center">
      <div className="w-12 h-12 bg-purple-100 rounded-card flex items-center justify-center mx-auto mb-4">
        <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.9 3.9 0 00-1.071 2.71A1.5 1.5 0 0113.5 21h-3a1.5 1.5 0 01-1.5-1.5 3.9 3.9 0 00-1.072-2.71l-.347-.347z" />
        </svg>
      </div>
      <p className="text-gray-500 text-sm">향후 추가 예정</p>
    </div>
  );
}

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────

export default function ExternalServices() {
  const [activeTab, setActiveTab] = useState<TabKey>('sms');

  const { data: status, isLoading: statusLoading, error: statusError, refetch } = useQuery({
    queryKey: ['admin', 'external-services'],
    queryFn: async (): Promise<ExternalServicesStatus> => {
      const response = await adminApi.getExternalServicesStatus();
      if (!response.data) throw new Error('상태 조회에 실패했습니다.');
      return response.data;
    },
  });

  const statusErrorMessage = statusError ? extractApiError(statusError).message : null;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">외부 서비스</h1>
        <Button variant="secondary" onClick={() => refetch()} disabled={statusLoading}>
          {statusLoading ? <SpinnerIcon /> : <RefreshIcon />}
          새로고침
        </Button>
      </div>

      {statusErrorMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-card text-red-700">
          {statusErrorMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'sms' ? (
        <div className="space-y-6">
          <SmsStatusSection status={status} loading={statusLoading} />
          <SmsPrefixSection />
          <SmsDailyStatsSection />
        </div>
      ) : (
        <AiServiceTab />
      )}
    </div>
  );
}
